import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LandingNav from '../landing/components/LandingNav';
import LandingFooter from '../landing/components/LandingFooter';

const stages = [
  {
    number: '01',
    title: 'Originate & Source',
    description: 'Identify and evaluate infrastructure opportunities through SeedDrive\'s curated deal flow engine. Apply filters by asset class, geography, return profile, and ESG criteria to build a qualified pipeline.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Diligence & Structure',
    description: 'Conduct structured due diligence with standardised IC memo templates, risk scoring frameworks, and integrated data room access. Collaborate with advisors and co-investors in a permissioned environment.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Form SPV & Syndicate',
    description: 'Automate SPV formation with jurisdiction-aware legal templates. Build the cap table, configure fee waterfalls, onboard investors through KYC/AML workflows, and manage commitment tracking in one place.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
      </svg>
    ),
  },
  {
    number: '04',
    title: 'Deploy & Monitor',
    description: 'Track capital deployment against milestones, monitor portfolio performance in real time, and receive ESG alerts. Role-based dashboards give investors, sponsors, and administrators the right view at the right time.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 3v18h18"/><path d="m19 9-5 5-4-4-3 3"/>
      </svg>
    ),
  },
  {
    number: '05',
    title: 'Report & Exit',
    description: 'Generate investor-ready reports, regulatory filings, and ESG disclosures with one click. Manage exit processes, distribution waterfalls, and post-investment documentation through to final settlement.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
      </svg>
    ),
  },
];

const roleWalkthroughs = {
  Investor: [
    { stage: 'Originate & Source', action: 'Browse curated deal flow, apply investment criteria filters, and bookmark opportunities for review.' },
    { stage: 'Diligence & Structure', action: 'Access data room documents, review IC memos, and submit investment committee questions.' },
    { stage: 'Form SPV & Syndicate', action: 'Complete KYC/AML onboarding, sign subscription documents, and confirm commitment amounts.' },
    { stage: 'Deploy & Monitor', action: 'Track portfolio performance, receive distribution notices, and monitor ESG metrics via investor dashboard.' },
    { stage: 'Report & Exit', action: 'Download tax packs, review exit valuations, and receive final distribution statements.' },
  ],
  Sponsor: [
    { stage: 'Originate & Source', action: 'Upload deal information, set access permissions, and invite co-investors to the opportunity.' },
    { stage: 'Diligence & Structure', action: 'Manage data room content, respond to investor queries, and coordinate legal structuring.' },
    { stage: 'Form SPV & Syndicate', action: 'Configure SPV parameters, set fee structures, and manage investor onboarding progress.' },
    { stage: 'Deploy & Monitor', action: 'Update milestone progress, publish portfolio updates, and manage procurement workflows.' },
    { stage: 'Report & Exit', action: 'Generate regulatory reports, manage exit timelines, and distribute proceeds via waterfall calculator.' },
  ],
  Admin: [
    { stage: 'Originate & Source', action: 'Oversee deal pipeline health, manage platform-wide access controls, and monitor compliance flags.' },
    { stage: 'Diligence & Structure', action: 'Audit document access logs, manage user permissions, and ensure regulatory requirements are met.' },
    { stage: 'Form SPV & Syndicate', action: 'Approve KYC/AML outcomes, manage jurisdiction configurations, and oversee SPV formation workflows.' },
    { stage: 'Deploy & Monitor', action: 'Monitor platform activity, manage role assignments, and review system audit trails.' },
    { stage: 'Report & Exit', action: 'Generate compliance reports, manage data retention policies, and oversee platform-wide reporting.' },
  ],
};

export default function HowItWorksPage() {
  const [activeRole, setActiveRole] = useState('Investor');

  return (
    <div className="min-h-screen" style={{ background: '#F2F0EC' }}>
      <LandingNav />
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-8" style={{ background: '#F2F0EC' }}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-caption font-medium mb-6 border" style={{ borderColor: 'rgba(59,76,76,0.15)', color: 'var(--color-secondary)', background: 'rgba(59,76,76,0.04)' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#3b4c4c' }}></span>
              How it works
            </div>
            <h1 className="font-heading text-4xl lg:text-5xl font-semibold leading-tight mb-5" style={{ color: 'var(--color-primary)' }}>Five stages. One seamless workflow.</h1>
            <p className="text-lg text-gray-600 font-body leading-relaxed">SeedDrive guides every deal from origination to exit through a structured, compliance-aware process designed for institutional infrastructure equity.</p>
          </div>
        </div>
      </section>
      {/* Workflow stages */}
      <section className="">
        <div className="max-w-5xl mx-auto">
          <div className="space-y-0">
            {stages?.map((stage, i) => (
              <div key={stage?.number} className="relative">
                <div className="flex gap-8 items-start">
                  {/* Left: number + connector */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center shadow-sm"
                      style={{ background: 'var(--color-primary)', color: 'white' }}
                    >
                      {stage?.icon}
                    </div>
                    {i < stages?.length - 1 && (
                      <div className="w-px flex-1 my-2" style={{ background: 'var(--color-border)', minHeight: '40px' }}></div>
                    )}
                  </div>
                  {/* Right: content */}
                  <div className="pb-10 flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-data text-xs font-medium" style={{ color: 'var(--color-primary)' }}>{stage?.number}</span>
                      <h3 className="font-heading text-xl font-semibold" style={{ color: 'var(--color-primary)' }}>{stage?.title}</h3>
                    </div>
                    <p className="text-gray-600 font-body leading-relaxed max-w-xl">{stage?.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Role walkthrough */}
      <section className="py-20 px-6 lg:px-8" style={{ background: '#ECEAE4' }}>
        <div className="max-w-5xl mx-auto">
          <div className="mb-10">
            <h2 className="font-heading text-3xl font-semibold mb-3" style={{ color: 'var(--color-primary)' }}>Your role in the workflow</h2>
            <p className="text-gray-600 font-body mb-7">Select your role to see what you do at each stage.</p>
            <div className="inline-flex rounded-lg border p-1" style={{ borderColor: 'var(--color-border)', background: 'white' }}>
              {Object.keys(roleWalkthroughs)?.map((role) => (
                <button
                  key={role}
                  onClick={() => setActiveRole(role)}
                  className={`px-5 py-2 rounded-md text-sm font-caption font-medium transition-all duration-200 ${
                    activeRole === role ? 'text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  style={activeRole === role ? { background: 'var(--color-primary)' } : {}}
                >
                  {role}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {roleWalkthroughs?.[activeRole]?.map((item, i) => (
              <div
                key={item?.stage}
                className=""
                style={{ borderColor: 'var(--color-border)' }}
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-data font-medium flex-shrink-0 mt-0.5"
                  style={{ background: 'rgba(59,76,76,0.08)', color: 'var(--color-primary)' }}
                >
                  {String(i + 1)?.padStart(2, '0')}
                </div>
                <div>
                  <div className="text-sm font-caption font-semibold mb-1" style={{ color: 'var(--color-primary)' }}>{item?.stage}</div>
                  <div className="text-sm font-body text-gray-600">{item?.action}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl font-semibold mb-3" style={{ color: 'var(--color-primary)' }}>Ready to get started?</h2>
          <p className="text-gray-600 font-body mb-7">Begin your onboarding or request a guided walkthrough from our team.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/deals/onboarding"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium font-caption text-white transition-all duration-200 hover:opacity-90"
              style={{ background: 'var(--color-primary)' }}
            >
              Get Started
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium font-caption border transition-all duration-200 hover:bg-gray-50"
              style={{ borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
            >
              Request Demo
            </Link>
          </div>
        </div>
      </section>
      <LandingFooter />
    </div>
  );
}
